import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getApiUrl } from '../config/api';
import { ACCESO, mensajeError, hojasDe, coincide } from './dpermisosUtil';

// Excepciones de acceso de un usuario respecto de su grupo
const PERMITIR = 'P';
const DENEGAR = 'D';

const urlUsuario = (usuario, resto) => getApiUrl(`/api/usuarios/${encodeURIComponent(usuario)}/${resto}`);

// Misma regla que el backend (AccesosUsuarioDTO.puedeVer): denegar por defecto
const puedeVer = (id, { delGrupo, excepciones }) => {
    const exc = excepciones.get(id);
    if (exc === DENEGAR) return false;
    return delGrupo.has(id) || exc === PERMITIR;
};

function NodoAcceso({ nodo, nivel, ctx }) {
    const { delGrupo, excepciones, grupoRestringe, filtro, soloExcepciones, cerrados, onPlegar, onCambiar } = ctx;
    const hojas = hojasDe(nodo);
    if (!coincide(nodo, filtro)) return null;
    if (soloExcepciones && !hojas.some(id => excepciones.has(id))) return null;

    const esPadre = nodo.hijos.length > 0;
    const sangria = { paddingLeft: 8 + nivel * 22 };

    if (esPadre) {
        const abierto = filtro || soloExcepciones ? true : !cerrados.has(nodo.id);
        const visibles = hojas.filter(id => puedeVer(id, ctx)).length;
        return (
            <div>
                <div className="dperm-nodo dperm-nodo-padre" style={sangria}>
                    <button type="button" className="dperm-plegar" onClick={() => onPlegar(nodo.id)}
                            title={abierto ? 'Contraer' : 'Expandir'}>{abierto ? '▾' : '▸'}</button>
                    <label><span>{nodo.descripcion}</span><span className="dperm-contador">ve {visibles}/{hojas.length}</span></label>
                </div>
                {abierto && nodo.hijos.map(h => <NodoAcceso key={h.id} nodo={h} nivel={nivel + 1} ctx={ctx} />)}
            </div>
        );
    }

    const exc = excepciones.get(nodo.id) || '';
    const visible = puedeVer(nodo.id, ctx);
    return (
        <div className="dperm-nodo dperm-nodo-usuario" style={sangria}>
            <span className="dperm-plegar" />
            <span className="dperm-nodo-nombre">
                {nodo.descripcion}
                {grupoRestringe && delGrupo.has(nodo.id) && <span className="dperm-badge">del grupo</span>}
            </span>
            <span className="dperm-seg">
                <button type="button" className={exc === '' ? 'activo' : ''} onClick={() => onCambiar(nodo.id, '')}
                        title="Sin excepcion: aplica lo que tenga el grupo">Heredar</button>
                <button type="button" className={exc === PERMITIR ? 'activo permitir' : ''} onClick={() => onCambiar(nodo.id, PERMITIR)}
                        title="El usuario ve esta opcion aunque su grupo no la tenga">Permitir</button>
                <button type="button" className={exc === DENEGAR ? 'activo denegar' : ''} onClick={() => onCambiar(nodo.id, DENEGAR)}
                        title="El usuario no ve esta opcion aunque su grupo la tenga">Denegar</button>
            </span>
            <span className={`dperm-resultado ${visible ? 'visible' : ''}`}>{visible ? 'Visible' : 'Oculto'}</span>
        </div>
    );
}

/**
 * Permisos individuales de un usuario: excepciones de acceso al menu y permisos
 * especiales propios, sobre lo que hereda de su grupo.
 */
function PermisosUsuario({ usuarioSel, grupos, menus, arbol, idsHojas, catalogo, operador, onAsignarGrupo, onCambio, onPendiente, onEliminado }) {
    const { usuario, grupo } = usuarioSel;

    const [subTab, setSubTab] = useState('accesos');             // accesos | especiales
    const [accesos, setAccesos] = useState(null);                // respuesta de GET /accesos
    const [excepciones, setExcepciones] = useState(new Map());   // idMenu -> P | D
    const [guardadas, setGuardadas] = useState(new Map());
    const [filtro, setFiltro] = useState('');
    const [soloExcepciones, setSoloExcepciones] = useState(false);
    const [cerrados, setCerrados] = useState(new Set());
    const [guardando, setGuardando] = useState(false);

    const [propios, setPropios] = useState([]);                  // permisos especiales cargados al usuario
    const [heredados, setHeredados] = useState([]);              // permisos especiales de su grupo
    const [nuevo, setNuevo] = useState({ idMenu: '', permiso: '', estado: 'A' });
    const [modalClave, setModalClave] = useState(null);         // { clave, clave2 }
    const [modalDatos, setModalDatos] = useState(null);         // { email }

    // ===== Carga =====

    const cargarAccesos = useCallback(async () => {
        try {
            const { data } = await axios.get(urlUsuario(usuario, 'accesos'));
            const exc = new Map([
                ...data.permitidos.map(id => [id, PERMITIR]),
                ...data.denegados.map(id => [id, DENEGAR])
            ]);
            setAccesos(data);
            setExcepciones(exc);
            setGuardadas(new Map(exc));
        } catch (e) { toast.error(mensajeError(e, 'Error al cargar los accesos del usuario')); }
    }, [usuario]);

    const cargarEspeciales = useCallback(async () => {
        try {
            const { data } = await axios.get(urlUsuario(usuario, 'permisos'));
            setPropios(data.filter(p => p.permiso !== ACCESO));
        } catch (e) { toast.error(mensajeError(e, 'Error al cargar los permisos del usuario')); }
        if (!grupo) { setHeredados([]); return; }
        try {
            const { data } = await axios.get(getApiUrl(`/api/grupos-usuario/${encodeURIComponent(grupo)}/permisos`));
            setHeredados(data.filter(p => p.permiso !== ACCESO));
        } catch (e) { setHeredados([]); }   // grupo heredado que no esta en el catalogo
    }, [usuario, grupo]);

    // grupo en las dependencias: al cambiar el grupo del usuario cambia lo heredado
    useEffect(() => { cargarAccesos(); }, [cargarAccesos, grupo]);
    useEffect(() => { cargarEspeciales(); }, [cargarEspeciales]);

    // ===== Accesos =====

    const hayCambios = excepciones.size !== guardadas.size || [...excepciones].some(([id, v]) => guardadas.get(id) !== v);
    useEffect(() => { onPendiente(hayCambios); }, [hayCambios, onPendiente]);

    const delGrupo = useMemo(() => new Set(accesos?.delGrupo || []), [accesos]);
    const grupoRestringe = !!accesos?.grupoRestringe;
    const reglas = { delGrupo, excepciones };
    const visibles = [...idsHojas].filter(id => puedeVer(id, reglas)).length;

    const cambiar = (id, valor) => setExcepciones(prev => {
        const m = new Map(prev);
        if (valor) m.set(id, valor); else m.delete(id);
        return m;
    });

    const plegar = (id) => setCerrados(prev => {
        const s = new Set(prev);
        if (s.has(id)) s.delete(id); else s.add(id);
        return s;
    });

    const guardarAccesos = async () => {
        // Evitar que el operador se quite a si mismo el acceso a esta pantalla
        const estaPantalla = menus.find(m => m.url === 'dpermisos');
        if (usuario === operador && estaPantalla && !puedeVer(estaPantalla.id, reglas)
            && !window.confirm('Esta quitandose a usted mismo el acceso a "Grupos y Permisos". '
                + 'No podra volver a esta pantalla desde el menu. ¿Guardar de todos modos?')) return;
        setGuardando(true);
        try {
            const ids = (tipo) => [...excepciones].filter(([id, v]) => v === tipo && idsHojas.has(id)).map(([id]) => id);
            await axios.put(urlUsuario(usuario, 'accesos'),
                { permitir: ids(PERMITIR), denegar: ids(DENEGAR), idOperador: operador || '' });
            toast.success('Excepciones de acceso guardadas');
            await cargarAccesos();
            onCambio();
        } catch (e) { toast.error(mensajeError(e, 'Error al guardar las excepciones')); }
        finally { setGuardando(false); }
    };

    const notaAccesos = () => {
        if (!accesos) return 'Cargando...';
        if (grupoRestringe) return `Hereda ${delGrupo.size} opcion(es) del grupo ${accesos.grupo}. Use Permitir para agregarle opciones y Denegar para quitarle alguna.`;
        return `${grupo ? `Su grupo ${grupo} no tiene accesos definidos` : 'No tiene grupo'}: solo vera las opciones que se le permitan aqui. Sin ninguna, no podra entrar a ningun lugar del sistema.`;
    };

    // ===== Permisos especiales =====

    const opcionesConPantalla = useMemo(
        () => menus.filter(m => m.url).sort((a, b) => (a.descripcion || '').localeCompare(b.descripcion || '')),
        [menus]);

    const agregarEspecial = async (e) => {
        e.preventDefault();
        const permiso = nuevo.permiso.trim();
        if (!nuevo.idMenu) { toast.warning('Seleccione la opcion de menu'); return; }
        if (!permiso) { toast.warning('Indique el permiso'); return; }
        if (permiso === ACCESO) { toast.warning('El acceso se define en la pestaña "Accesos al menu"'); return; }
        try {
            await axios.post(getApiUrl('/api/permisos'),
                { usuario, idMenu: Number(nuevo.idMenu), permiso, estado: nuevo.estado, idOperador: operador || '' });
            toast.success(nuevo.estado === 'A' ? 'Permiso concedido' : 'Permiso denegado');
            setNuevo({ idMenu: '', permiso: '', estado: 'A' });
            cargarEspeciales();
        } catch (err) { toast.error(mensajeError(err, 'Error al asignar el permiso')); }
    };

    const cambiarEstado = async (p) => {
        try {
            await axios.put(getApiUrl(`/api/permisos/${p.id}/estado`),
                { estado: p.estado === 'A' ? 'I' : 'A', idOperador: operador || '' });
            cargarEspeciales();
        } catch (e) { toast.error(mensajeError(e, 'Error al cambiar el estado')); }
    };

    const eliminar = async (p) => {
        if (!window.confirm(`¿Quitar la excepcion ${p.permiso} sobre "${p.menu}"? El usuario volvera a heredar lo de su grupo.`)) return;
        try {
            await axios.delete(getApiUrl(`/api/permisos/${p.id}`));
            cargarEspeciales();
        } catch (e) { toast.error(mensajeError(e, 'Error al quitar el permiso')); }
    };

    const propioSobre = (h) => propios.find(p => p.idMenu === h.idMenu && p.permiso === h.permiso);

    // ===== Clave y baja del usuario =====

    const guardarClave = async (e) => {
        e.preventDefault();
        if (modalClave.clave.length < 6) { toast.warning('La contrasena debe tener al menos 6 caracteres'); return; }
        if (modalClave.clave !== modalClave.clave2) { toast.warning('Las contrasenas no coinciden'); return; }
        try {
            await axios.put(urlUsuario(usuario, 'clave'), { clave: modalClave.clave, idOperador: operador || '' });
            toast.success(`Contrasena de ${usuario} actualizada`);
            setModalClave(null);
        } catch (err) { toast.error(mensajeError(err, 'Error al cambiar la contrasena')); }
    };

    const guardarDatos = async (e) => {
        e.preventDefault();
        try {
            await axios.put(getApiUrl(`/api/usuarios/${encodeURIComponent(usuario)}`), { email: modalDatos.email.trim(), grupo: grupo || null, idOperador: operador || '' });
            toast.success('Datos actualizados');
            setModalDatos(null);
            onCambio();
        } catch (err) { toast.error(mensajeError(err, 'Error al guardar los datos')); }
    };

    const eliminarUsuario = async () => {
        if (!window.confirm(`¿Eliminar el usuario ${usuario}? Se borran tambien sus permisos individuales.`)) return;
        try {
            await axios.delete(getApiUrl(`/api/usuarios/${encodeURIComponent(usuario)}`), { params: { idOperador: operador || '' } });
            toast.success(`Usuario ${usuario} eliminado`);
            onEliminado();
        } catch (e) { toast.error(mensajeError(e, 'Error al eliminar el usuario')); }
    };

    const ctx = { ...reglas, grupoRestringe, filtro: filtro.trim().toLowerCase(), soloExcepciones, cerrados, onPlegar: plegar, onCambiar: cambiar };

    return (
        <>
            <div className="dperm-detalle-header">
                <h3>
                    {usuario}{usuarioSel.nomEmpleado && <span> — {usuarioSel.nomEmpleado}</span>}
                    <small className="dperm-email-detalle">{usuarioSel.email || 'sin correo'}</small>
                </h3>
                <div className="dperm-grupo-usuario">
                    <span>Grupo:</span>
                    <select className="form-input dperm-select-grupo" value={grupo || ''}
                            onChange={(e) => onAsignarGrupo(usuario, e.target.value)}>
                        <option value="">(sin grupo)</option>
                        {/* grupo heredado que aun no esta en el catalogo */}
                        {grupo && !grupos.some(g => g.idGrupo === grupo) && <option value={grupo}>{grupo} (no catalogado)</option>}
                        {grupos.map(g => <option key={g.idGrupo} value={g.idGrupo}>{g.idGrupo} — {g.nomGrupo}</option>)}
                    </select>
                    <button className="btn dperm-btn-sec" onClick={() => setModalDatos({ email: usuarioSel.email || '' })}>Correo</button>
                    <button className="btn dperm-btn-sec" onClick={() => setModalClave({ clave: '', clave2: '' })}>Cambiar clave</button>
                    <button className="btn btn-cancelar" onClick={eliminarUsuario} disabled={usuario === operador}
                            title={usuario === operador ? 'No puede eliminar el usuario con el que esta trabajando' : ''}>Eliminar</button>
                </div>
            </div>

            <div className="dperm-tabs dperm-subtabs">
                <button className={subTab === 'accesos' ? 'activo' : ''} onClick={() => setSubTab('accesos')}>Accesos al menu ({guardadas.size} excepcion(es))</button>
                <button className={subTab === 'especiales' ? 'activo' : ''} onClick={() => setSubTab('especiales')}>Permisos especiales ({propios.length})</button>
            </div>

            {subTab === 'accesos' && (
                <div>
                    <div className="dperm-barra">
                        <input type="text" className="input-busqueda" placeholder="Buscar opcion..."
                               value={filtro} onChange={(e) => setFiltro(e.target.value)} />
                        <label className="dperm-check">
                            <input type="checkbox" checked={soloExcepciones} onChange={(e) => setSoloExcepciones(e.target.checked)} />
                            Solo excepciones
                        </label>
                        <button className="btn dperm-btn-sec" disabled={excepciones.size === 0}
                                onClick={() => setExcepciones(new Map())}>Quitar excepciones</button>
                        <span className="dperm-resumen">Vera {visibles} de {idsHojas.size} opciones</span>
                        <button className="btn btn-guardar" disabled={!hayCambios || guardando} onClick={guardarAccesos}>
                            {guardando ? 'Guardando...' : hayCambios ? 'Guardar excepciones' : 'Sin cambios'}
                        </button>
                    </div>
                    <p className="dperm-nota">{notaAccesos()} Los cambios se aplican cuando el usuario vuelve a iniciar sesion.</p>
                    <div className="dperm-arbol">
                        {arbol.map(n => <NodoAcceso key={n.id} nodo={n} nivel={0} ctx={ctx} />)}
                        {soloExcepciones && excepciones.size === 0 && <p className="dperm-vacio">El usuario no tiene excepciones de acceso.</p>}
                    </div>
                </div>
            )}

            {subTab === 'especiales' && (
                <div>
                    <form className="dperm-barra" onSubmit={agregarEspecial}>
                        <select className="form-input" value={nuevo.idMenu} onChange={(e) => setNuevo({ ...nuevo, idMenu: e.target.value })}>
                            <option value="">Opcion de menu...</option>
                            {opcionesConPantalla.map(m => <option key={m.id} value={m.id}>{m.descripcion}</option>)}
                        </select>
                        <input className="form-input" list="dperm-catalogo-usuario" placeholder="Permiso (ej: Factura_timbrado_vencido)"
                               maxLength={30} value={nuevo.permiso} onChange={(e) => setNuevo({ ...nuevo, permiso: e.target.value })} />
                        <datalist id="dperm-catalogo-usuario">
                            {catalogo.filter(p => p !== ACCESO).map(p => <option key={p} value={p} />)}
                        </datalist>
                        <select className="form-input dperm-select-corto" value={nuevo.estado} onChange={(e) => setNuevo({ ...nuevo, estado: e.target.value })}>
                            <option value="A">Conceder</option>
                            <option value="I">Denegar</option>
                        </select>
                        <button type="submit" className="btn btn-nuevo">+ Asignar</button>
                    </form>

                    <h4 className="dperm-subtitulo">Propios del usuario</h4>
                    <div className="tabla-container">
                        <table className="tabla-datos">
                            <thead><tr><th>Opcion de menu</th><th>Permiso</th><th>Efecto</th><th>Acciones</th></tr></thead>
                            <tbody>
                                {propios.length === 0 ? (
                                    <tr><td colSpan="4" className="sin-datos">Sin permisos especiales propios: aplica lo de su grupo</td></tr>
                                ) : propios.map(p => (
                                    <tr key={p.id}>
                                        <td>{p.menu}</td>
                                        <td><strong>{p.permiso}</strong></td>
                                        <td>
                                            <button className={`dperm-estado ${p.estado === 'A' ? 'activo' : 'denegado'}`}
                                                    onClick={() => cambiarEstado(p)} title="Cambiar entre conceder y denegar">
                                                {p.estado === 'A' ? 'Concedido' : 'Denegado'}
                                            </button>
                                        </td>
                                        <td><button className="btn-accion" title="Quitar la excepcion" onClick={() => eliminar(p)}>🗑️</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <h4 className="dperm-subtitulo">Heredados del grupo {grupo || '(sin grupo)'}</h4>
                    <div className="tabla-container">
                        <table className="tabla-datos">
                            <thead><tr><th>Opcion de menu</th><th>Permiso</th><th>Para este usuario</th></tr></thead>
                            <tbody>
                                {heredados.length === 0 ? (
                                    <tr><td colSpan="3" className="sin-datos">El grupo no tiene permisos especiales</td></tr>
                                ) : heredados.map(h => {
                                    const propio = propioSobre(h);
                                    const aplica = propio ? propio.estado === 'A' : h.estado === 'A';
                                    return (
                                        <tr key={h.id}>
                                            <td>{h.menu}</td>
                                            <td><strong>{h.permiso}</strong></td>
                                            <td>
                                                <span className={`dperm-resultado ${aplica ? 'visible' : ''}`}>{aplica ? 'Aplica' : 'No aplica'}</span>
                                                {propio && <small className="dperm-detalle-efecto"> (por excepcion del usuario)</small>}
                                                {!propio && h.estado !== 'A' && <small className="dperm-detalle-efecto"> (inactivo en el grupo)</small>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {modalDatos && (
                <div className="modal-overlay">
                    <div className="modal-contenido" style={{ maxWidth: '420px' }}>
                        <div className="modal-header">
                            <h3>Correo de {usuario}</h3>
                            <button onClick={() => setModalDatos(null)} className="btn-cerrar">&times;</button>
                        </div>
                        <form onSubmit={guardarDatos} autoComplete="off">
                            <div className="form-group">
                                <label>Correo electronico (vacio para quitarlo)</label>
                                <input type="email" className="form-input" value={modalDatos.email} maxLength={120} autoFocus
                                       autoComplete="off" onChange={(e) => setModalDatos({ email: e.target.value })} />
                            </div>
                            <div className="modal-footer">
                                <button type="button" onClick={() => setModalDatos(null)} className="btn btn-cancelar">Cancelar</button>
                                <button type="submit" className="btn btn-guardar">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {modalClave && (
                <div className="modal-overlay">
                    <div className="modal-contenido" style={{ maxWidth: '420px' }}>
                        <div className="modal-header">
                            <h3>Cambiar contrasena de {usuario}</h3>
                            <button onClick={() => setModalClave(null)} className="btn-cerrar">&times;</button>
                        </div>
                        <form onSubmit={guardarClave} autoComplete="off">
                            <div className="form-group">
                                <label>Contrasena nueva * (minimo 6 caracteres)</label>
                                <input type="password" className="form-input" value={modalClave.clave} maxLength={72} autoFocus
                                       autoComplete="new-password" onChange={(e) => setModalClave({ ...modalClave, clave: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Repetir contrasena *</label>
                                <input type="password" className="form-input" value={modalClave.clave2} maxLength={72}
                                       autoComplete="new-password" onChange={(e) => setModalClave({ ...modalClave, clave2: e.target.value })} />
                            </div>
                            <div className="modal-footer">
                                <button type="button" onClick={() => setModalClave(null)} className="btn btn-cancelar">Cancelar</button>
                                <button type="submit" className="btn btn-guardar">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}

export default PermisosUsuario;
